import manifest from '../data/publication-scope.json'

export interface CollectionPublicationScope {
	sourceCount: number
	allowedIds: string[]
	excludedIds: string[]
	sourceSha256: string
}
export interface PublicationScope {
	version: 1
	policy: 'approved-staged-previously-published-non-draft-non-archived'
	auditedAt: string
	unknownIdPolicy: 'deny'
	collections: Record<string, CollectionPublicationScope>
}

export const publicationScope = manifest as PublicationScope
const allowed = new Map(Object.entries(publicationScope.collections).map(([name, scope]) => [name, new Set(scope.allowedIds)]))

/** Unknown/editor-created records remain private until explicitly reviewed into scope. */
export function isPublicRecord(collection: string, row: Record<string, unknown>): boolean {
	return typeof row._id === 'string' && (allowed.get(collection)?.has(row._id) ?? false)
}

/** Filter BEFORE pagination, relation expansion, search indexing, or deriving slugs. */
export function publicRecords<T extends Record<string, unknown>>(collection: string, rows: T[]): T[] {
	if (!allowed.has(collection)) throw new Error(`Unaudited public collection: ${collection}`)
	return rows.filter(row => isPublicRecord(collection, row))
}

/** Build gate: excluded IDs may remain in admin; unknown IDs must be reviewed. */
export function assertPublicationInventory(collection: string, rows: Array<Record<string, unknown>>): void {
	const scope = publicationScope.collections[collection]
	if (!scope) throw new Error(`Unaudited public collection: ${collection}`)
	const known = new Set([...scope.allowedIds, ...scope.excludedIds])
	const seen = new Set<string>()
	for (const row of rows) {
		if (typeof row._id !== 'string' || !known.has(row._id)) throw new Error(`Unknown source ID in ${collection}; explicit publication approval required`)
		if (seen.has(row._id)) throw new Error(`Duplicate source ID in ${collection}: ${row._id}`)
		seen.add(row._id)
	}
	const missing = scope.allowedIds.filter(id => !seen.has(id))
	if (missing.length) throw new Error(`Missing ${missing.length} approved source IDs in ${collection}`)
}

export function assertPublicationFreshness(now = new Date()): void {
	const captured = Date.parse(publicationScope.auditedAt)
	if (!Number.isFinite(captured) || captured > now.getTime() || now.getTime() - captured > 7 * 86400000) {
		throw new Error('Publication audit expired; refresh and explicitly approve all source collections')
	}
}
